import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component, EventEmitter,
  forwardRef,
  Input, Output,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from "@angular/forms";
import {
  FilteredComponent,
  FilterModel,
  LanguagesQuery,
  LeModalService,
  PropertiesQuery, stringCompare
} from "common-shared";
import {BehaviorSubject, noop} from "rxjs";
import {NgbModalRef} from "@ng-bootstrap/ng-bootstrap";
import {LazyLoadEvent} from "primeng/api";
import {takeUntil} from "rxjs/operators";
import {NgSelectComponent} from "@ng-select/ng-select";
import {ResourceEditModalComponent} from "../resource-edit-modal/resource-edit-modal.component";
import {IntegratedMasterListColumns} from "../../../core/table-column";
import {instanceToInstance} from "class-transformer";
import {ResourceListFilter, ResourceListQuery} from '../models/resource.query';
import {ResourcesQuery} from "../resource.store";
import {ResourceService} from "../resource.service";

@Component({
  selector: 'app-resource-picker',
  templateUrl: './resource-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [`
    :host {
      display: flex;
    }
  `],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => ResourcePickerComponent),
    multi: true
  }]
})
export class ResourcePickerComponent extends FilteredComponent implements ControlValueAccessor {

  resourceId: string | string[] = '';
  private onTouchedCallback: () => void = noop;
  public onChangeCallback: (_: any) => void = noop;

  filteredData$ = new BehaviorSubject<ResourceListQuery[]>([]);
  selectedResource: ResourceListQuery | ResourceListQuery[];
  pickerBsModalRef: NgbModalRef;
  filter = new FilterModel<ResourceListFilter>({Data: new ResourceListFilter, Page: this.page});
  search$ = new BehaviorSubject<string>(null);
  loading$ = new BehaviorSubject<boolean>(true);

  @Input() name: string;
  @Input() readonly: boolean = false;
  @Input() multiple: boolean = false;
  @Input() showEdit: boolean = true;
  @Input() showSearch: boolean = true;
  @Input() groupByToConnections = false;
  @Input() placeholder: string = '';
  @Input() addTag: boolean | ((term: string) => any | Promise<any>) = false;
  @Input() addTagText: string = 'Add new';
  @Output() focus = new EventEmitter<NgSelectComponent>();

  @ViewChild('selectCmp') ngSelect: NgSelectComponent;
  tableDropdownClosed: EventEmitter<void> = new EventEmitter<void>();

  constructor(public resourcesQuery: ResourcesQuery, private modalService: LeModalService, private resourceService: ResourceService, public propertiesQuery: PropertiesQuery, private changeDetectorRef: ChangeDetectorRef, public languagesQuery: LanguagesQuery) {
    super();
  }

  ngOnInit(): void {
    this.resourcesQuery.selectLoading().pipe(takeUntil(this.componentDestroyed)).subscribe(value => {
      this.updateLoading(value);
    });
  }

  private updateLoading(value: boolean) {
    this.loading$.next(value);
    this.changeDetectorRef.detectChanges();
  }

  registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }

  writeValue(value: string | string[]): void {
    if (value !== this.resourceId) {
      this.resourceId = value;
    }
  }

  openPicker(templateRef: TemplateRef<any>) {
    const event: LazyLoadEvent = {
      filters: this.defaultFilter as any,
      sortOrder: this.sortOrder,
      sortField: this.sortField,
      multiSortMeta: this.multiSortMeta
    };

    this.filter.Page.Size = 50;
    this.filter.setFilters(event.filters);
    this.filter.Global = event.filters?.global?.value;
    delete this.filter.Data['global'];
    this.filter.setSort(event);

    this.pickerBsModalRef = this.modalService.bsModalService.open(templateRef, {windowClass: 'modal-full modal-fit-content', backdrop: "static", keyboard: false});
  }

  openSelectForEdit(mode: string) {
    if (mode === 'edit') {
      setTimeout(() => {
        this.ngSelect?.open()
        this.changeDetectorRef.detectChanges();
      });
    }
  }

  onSelectOpen() {
    this.filter.Page.Size = 0;
    this.filter.Data.ForSelect = true;
    this.filter.Data.IncludeToConnectionsInSelect = this.groupByToConnections;
    this.filteredData$.next([]);
    this.load({
      filters: this.defaultFilter as any,
      sortOrder: this.sortOrder,
      sortField: this.sortField,
      multiSortMeta: this.multiSortMeta
    });
  }

  load(event: LazyLoadEvent = null) {
    this.updateLoading(true);
    if (event) {
      this.filter.setPage(event);
      this.filter.setFilters(event.filters);
      this.filter.Global = event.filters?.global?.value;
      delete this.filter.Data['global'];
      this.filter.setSort(event);
    }
    this.resourceService.getByFilter(this.filter).pipe(takeUntil(this.componentDestroyed)).subscribe(pagedData => {
      this.filteredData$.next(this.filter.Data.ForSelect && this.filter.Data.IncludeToConnectionsInSelect ? this.getDataGroupedByToConnections(pagedData.Data) : pagedData.Data);
      this.filter.Page = pagedData.Page;
      this.updateLoading(false);
    });
  }

  getDataGroupedByToConnections(resources: ResourceListQuery[]) {
    let list = [];
    let groups = resources.flatMap(resource => resource.ToResourceConnections) as any as string[];
    let groupResources = [...new Set(groups)].map(group => this.resourcesQuery.getEntity(group));
    groupResources.sort((a, b) => stringCompare(a, b, obj => obj.Code)).forEach(group => {
      resources.forEach(resource => {
        if ((resource.ToResourceConnections as any as string[]).includes(group.Id)) {
          const item = instanceToInstance(resource);
          item['Group'] = group.DisplayName;
          list.push(item);
        }
      });
    });

    return list;
  }

  select() {
    if (this.multiple) {
      this.onChangeCallback([...this.resourceId]);
    } else {
      this.onChangeCallback(this.resourceId);
    }
  }

  selectAndClose() {
    this.selectedResource instanceof ResourceListQuery ? this.resourceId = this.selectedResource.Id : this.resourceId = this.selectedResource.map(value => value.Id);
    this.pickerBsModalRef.close();
    this.onChangeCallback(this.resourceId);
  }

  open() {
    this.modalService.show(ResourceEditModalComponent, {initialState: {entityId: this.multiple ? this.resourceId[0] : this.resourceId} as ResourceEditModalComponent, class: 'modal-xl'}).onHide$.pipe(takeUntil(this.componentDestroyed)).subscribe((bsModalRef) => {
      const comp = bsModalRef.content as ResourceEditModalComponent;
      if (comp.entityId && !this.multiple) {
        this.resourceId = comp.entityId;
      }
    });
  }

  protected readonly IntegratedMasterListColumns = IntegratedMasterListColumns;

  clearAll() {
    if (this.multiple) {
      this.resourceId = [];
    } else {
      this.resourceId = null;
    }
    this.onChangeCallback(this.resourceId);
  }
}
